import { sprintf } from 'sprintf-js';

import type { ApiAreaOfFocus } from '#/api/types/ApiAreaOfFocus';
import { DetailsList } from '#/components/DetailsList/DetailsList';
import { DetailsListItem } from '#/components/DetailsList/Item/DetailsListItem';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nFn } from '#/i18n/types/I18nFn';
import { ListingDateDetailsListItems } from '#/modules/listing/components/Date/ListingDateDetailsListItems';
import type {
  IanaTimezone,
  DateString,
  TimeString,
} from '#/types/TypedDateString';
import { AREAS_OF_FOCUS } from '#/utils/constants/general/areasOfFocus';
import { EXPECTED_TIMES } from '#/utils/constants/general/expectedTimes';
import { EXPECTED_TIMES_ONCE } from '#/utils/constants/general/expectedTimesOnce';
import { EXPECTED_TIMES_RECURRING } from '#/utils/constants/general/expectedTimesRecurring';
import type { TimeOfDay } from '#/utils/constants/general/timesOfDay';

const formatAmountString = (amount: string) => {
  const parts = amount.split('.');
  const cents = parseInt(parts[1], 10);
  const [dollars] = parts;
  return `$${dollars}${cents > 0 ? `.${cents}` : ''}`;
};

const formatTimesOfDay = (i18n: I18nFn, timesOfDay: TimeOfDay[]) => {
  const weekdays = [];
  const weekends = [];

  if (timesOfDay.includes('WEEKDAYS_DAYTIME')) weekdays.push(i18n('daytime'));
  if (timesOfDay.includes('WEEKDAYS_EVENINGS')) weekdays.push(i18n('evenings'));
  if (timesOfDay.includes('WEEKENDS_DAYTIME')) weekends.push(i18n('daytime'));
  if (timesOfDay.includes('WEEKENDS_EVENINGS')) weekends.push(i18n('evenings'));

  // Weekends (daytime, evenings), Weekdays (daytime, evenings)
  return [
    weekdays.length > 0 &&
      sprintf(`%(weekPart)s (%(dayPart)s)`, {
        weekPart: i18n('Weekdays'),
        dayPart: weekdays.join(', '),
      }),
    weekends.length > 0 &&
      sprintf(`%(weekPart)s (%(dayPart)s)`, {
        weekPart: i18n('Weekends'),
        dayPart: weekends.join(', '),
      }),
  ]
    .filter(Boolean)
    .join(', ');
};

type Props = {
  areasOfFocus: ApiAreaOfFocus[];
  startDate: DateString | null;
  startTime: TimeString | null;
  endDate: DateString | null;
  endTime: TimeString | null;
  timezone: IanaTimezone | null;
  feeAmount: string | null;
  feeRequired: boolean;
  driversLicenseRequired: boolean;
  backgroundCheckRequired: boolean;
  orientationRequired: boolean;
  isRecurring: boolean;
  welcomeFamilies: boolean;
  welcomeTeens: boolean;
  welcomeGroups: boolean;
  welcomeAge55Plus: boolean;
  welcomeIntl: boolean;
  welcomePrivateCorpGroups: boolean;
  detailsStipendProvided: boolean;
  detailsTrainingProvided: boolean;
  detailsHousingAvailable: boolean;
  detailsCulturalSupport: boolean;
  detailsAcademicCreditAvailable: boolean;
  detailsWheelchairAccessible: boolean;
  timesOfDay: TimeOfDay[];
  expectedTime: string | null;
  ageRequirement: number | null;
  otherRequirements: string | null;
  numVolunteersNeeded: number | null;
  commitmentDetails: string | null;
  canBeDoneInADay?: boolean;
};

function REQUIREMENTS_LABELS(i18n: I18nFn): Record<string, string> {
  return {
    driversLicenseRequired: i18n('Driver’s License'),
    backgroundCheckRequired: i18n('Background Check'),
    orientationRequired: i18n('Attend Orientation'),
  };
}

const WELCOME_LABELS = (i18n: I18nFn): Record<string, string> => ({
  welcomeFamilies: i18n('Kids'),
  welcomeTeens: i18n('Teens'),
  welcomeGroups: i18n('Public Groups'),
  welcomeAge55Plus: i18n('Age 55+'),
  welcomeIntl: i18n('International Volunteers'),
  welcomePrivateCorpGroups: i18n('Private Corporate Groups'),
});

const BENEFIT_LABELS = (i18n: I18nFn): Record<string, string> => ({
  detailsStipendProvided: i18n('Stipend Provided'),
  detailsTrainingProvided: i18n('Training Provided'),
  detailsHousingAvailable: i18n('Housing Available'),
  detailsCulturalSupport: i18n('Language / Cultural Support Available'),
  detailsAcademicCreditAvailable: i18n('Academic Credit Available'),
});

export function VolopDetailsList({
  areasOfFocus,
  startDate,
  startTime,
  endDate,
  endTime,
  timezone,
  driversLicenseRequired,
  backgroundCheckRequired,
  orientationRequired,
  feeRequired,
  feeAmount,
  isRecurring,
  welcomeFamilies,
  welcomeTeens,
  welcomeGroups,
  welcomeAge55Plus,
  welcomeIntl,
  welcomePrivateCorpGroups,
  detailsStipendProvided,
  detailsTrainingProvided,
  detailsHousingAvailable,
  detailsCulturalSupport,
  detailsAcademicCreditAvailable,
  detailsWheelchairAccessible,
  timesOfDay,
  expectedTime,
  ageRequirement,
  otherRequirements,
  numVolunteersNeeded,
  commitmentDetails,
  canBeDoneInADay,
}: Props) {
  const { i18n } = useI18n();

  const formattedExpectedTime =
    expectedTime === 'NONE'
      ? null
      : [
          ...EXPECTED_TIMES(i18n),
          ...EXPECTED_TIMES_ONCE(i18n),
          ...EXPECTED_TIMES_RECURRING(i18n),
        ].find(({ id }) => id === expectedTime);

  const allRequirements = [
    driversLicenseRequired && 'driversLicenseRequired',
    backgroundCheckRequired && 'backgroundCheckRequired',
    orientationRequired && 'orientationRequired',
  ].filter(Boolean);

  const allWelcome = [
    welcomeFamilies && 'welcomeFamilies',
    welcomeTeens && 'welcomeTeens',
    welcomeGroups && 'welcomeGroups',
    welcomeAge55Plus && 'welcomeAge55Plus',
    welcomeIntl && 'welcomeIntl',
    welcomePrivateCorpGroups && 'welcomePrivateCorpGroups',
  ].filter(Boolean);

  const allBenefits = [
    detailsStipendProvided && 'detailsStipendProvided',
    detailsTrainingProvided && 'detailsTrainingProvided',
    detailsHousingAvailable && 'detailsHousingAvailable',
    detailsCulturalSupport && 'detailsCulturalSupport',
    detailsAcademicCreditAvailable && 'detailsAcademicCreditAvailable',
  ].filter(Boolean);

  return (
    <DetailsList>
      {startDate && (
        <ListingDateDetailsListItems
          startDate={startDate}
          startTime={startTime}
          endDate={endDate}
          endTime={endTime}
          timezone={timezone}
        />
      )}

      {timesOfDay.length > 0 && (
        <DetailsListItem
          title={i18n('Available Times')}
          iconName="calendar"
          value={formatTimesOfDay(i18n, timesOfDay)}
        />
      )}

      {formattedExpectedTime && (
        <DetailsListItem
          title={i18n('Time Commitment')}
          iconName="clock"
          value={formattedExpectedTime.name}
        />
      )}

      {commitmentDetails && (
        <DetailsListItem
          title={i18n('Commitment Details')}
          iconName="clock"
          value={commitmentDetails}
        />
      )}

      <DetailsListItem
        title={i18n('Recurrence')}
        iconName="hourglass"
        value={isRecurring ? i18n('Recurring') : i18n('One time only')}
      />

      {canBeDoneInADay && (
        <DetailsListItem iconName="hammer" value={i18n('Done in a Day')} />
      )}

      {numVolunteersNeeded && (
        <DetailsListItem
          title={i18n('Volunteers Needed')}
          iconName="two-people"
          value={numVolunteersNeeded}
        />
      )}

      {feeRequired && (
        <DetailsListItem
          title={i18n('Cost')}
          iconName="coin"
          value={`${i18n(
            'Fee Required',
          )}${feeAmount ? `: ${formatAmountString(feeAmount)}` : ''}`}
        />
      )}

      {areasOfFocus?.length !== 0 && (
        <DetailsListItem
          iconName="globe"
          title={i18n('Cause Areas')}
          value={areasOfFocus
            .map((item) => AREAS_OF_FOCUS(i18n)[item])
            .join(', ')}
        />
      )}

      {allBenefits.length > 0 && (
        <DetailsListItem
          iconName="percentage-award"
          title={i18n('Benefits')}
          value={allBenefits.map((k) => BENEFIT_LABELS(i18n)[k]).join(', ')}
        />
      )}

      {allWelcome.length > 0 && (
        <DetailsListItem
          iconName="group"
          title={i18n('Good For')}
          value={allWelcome
            .map((item) => WELCOME_LABELS(i18n)[item])
            .join(', ')}
        />
      )}

      {allRequirements.length > 0 && (
        <DetailsListItem
          iconName="paper"
          title={i18n('Participation Requirements')}
          value={allRequirements
            .map((k) => REQUIREMENTS_LABELS(i18n)[k])
            .join(', ')}
        />
      )}

      {ageRequirement && (
        <DetailsListItem
          iconName="cms/cms-persona-quiz"
          title={i18n('Age Requirement')}
          value={`${ageRequirement}+`}
        />
      )}

      {otherRequirements && (
        <DetailsListItem
          iconName="clipboard"
          title={i18n('Other Requirements')}
          value={otherRequirements}
        />
      )}

      {detailsWheelchairAccessible && (
        <DetailsListItem
          iconName="wheelchair"
          value={i18n('Wheelchair Accessible')}
        />
      )}
    </DetailsList>
  );
}
